'use client';

import {
  Autocomplete as MUIAutocomplete,
  TextField,
  type AutocompleteProps,
  type FilterOptionsState,
  type TextFieldProps,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDebounce } from '@web/hooks';

export type AutocompleteAsyncProps<
  TOption,
  Multiple extends boolean | undefined = false,
> = Omit<
  AutocompleteProps<TOption, Multiple, false, false>,
  'renderInput' | 'options' | 'loading' | 'onInputChange'
> & {
  // * Provide paginated results for a query + page.
  loadOptions: (args: { query: string; page: number }) => Promise<{
    options: TOption[];
    hasMore: boolean;
  }>;
  onLoadError?: (error: unknown) => void;
  debounceMs?: number;
  loadOnOpen?: boolean;
  loadOnInput?: boolean;
  noOptionsText?: string;
  paginationMode?: 'scroll' | 'button' | 'both';
  loadMoreText?: string;
  label?: string;
  placeholder?: string;
  textFieldProps?: TextFieldProps;
  inputValue?: string;
  onInputChange?: AutocompleteProps<
    TOption,
    Multiple,
    false,
    false
  >['onInputChange'];
};

export function AutocompleteAsync<
  TOption,
  Multiple extends boolean | undefined = false,
>({
  loadOptions,
  onLoadError,
  debounceMs = 300,
  loadOnOpen = true,
  loadOnInput = true,
  noOptionsText = 'No options',
  paginationMode = 'both',
  loadMoreText = 'Load more',
  label,
  placeholder,
  textFieldProps,
  inputValue,
  onInputChange,
  open: openProp,
  onOpen,
  onClose,
  ...autocompleteProps
}: AutocompleteAsyncProps<TOption, Multiple>) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [internalInputValue, setInternalInputValue] = useState('');
  const [options, setOptions] = useState<TOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const lastLoadedValueRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const lastQueryRef = useRef<string>('');

  const isOpenControlled = typeof openProp === 'boolean';
  const isInputControlled =
    inputValue !== undefined && typeof onInputChange === 'function';

  const open = isOpenControlled ? openProp : internalOpen;
  const currentInputValue = isInputControlled
    ? inputValue!
    : internalInputValue;

  const debouncedInputValue = useDebounce(currentInputValue, debounceMs);
  const query = debouncedInputValue.trim();

  const canLoadOnOpen = useMemo(
    () => loadOnOpen && open && query === '',
    [loadOnOpen, open, query],
  );

  type LoadMoreOption = { __autocompleteLoadMore: true; label: string };
  const loadMoreOption = useMemo<LoadMoreOption>(
    () => ({ __autocompleteLoadMore: true, label: loadMoreText }),
    [loadMoreText],
  );
  const isLoadMoreOption = useCallback(
    (option: unknown): option is LoadMoreOption =>
      typeof option === 'object' &&
      option !== null &&
      '__autocompleteLoadMore' in option,
    [],
  );

  const load = useCallback(
    async (nextQuery: string, nextPage: number) => {
      const requestKey = `${nextQuery}::${nextPage}`;
      if (lastLoadedValueRef.current === requestKey) return;
      lastLoadedValueRef.current = requestKey;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      if (nextPage === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      try {
        const result = await loadOptions({ query: nextQuery, page: nextPage });
        if (requestIdRef.current === requestId) {
          setHasMore(result.hasMore);
          setPage(nextPage);
          setOptions((prev) =>
            nextPage === 1 ? result.options : [...prev, ...result.options],
          );
        }
      } catch (error) {
        if (requestIdRef.current === requestId) {
          onLoadError?.(error);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [loadOptions, onLoadError],
  );

  useEffect(() => {
    if (!open) return;

    if (canLoadOnOpen) {
      void load('', 1);
    }
  }, [canLoadOnOpen, load, open]);

  useEffect(() => {
    if (!open) return;
    if (lastQueryRef.current !== query) {
      lastQueryRef.current = query;
      lastLoadedValueRef.current = null;
      setPage(1);
      setHasMore(false);
      setLoadingMore(false);
      setOptions([]);
    }

    if (!loadOnInput) return;
    if (!query && !loadOnOpen) return;

    void load(query, 1);
  }, [load, loadOnInput, loadOnOpen, open, query]);

  const handleOpen = (event: React.SyntheticEvent) => {
    if (!isOpenControlled) setInternalOpen(true);
    onOpen?.(event);
  };

  const handleClose = (event: React.SyntheticEvent) => {
    if (!isOpenControlled) setInternalOpen(false);
    onClose?.(event, 'blur');
  };

  const handleInputChange: AutocompleteAsyncProps<
    TOption,
    Multiple
  >['onInputChange'] = (event, newValue, reason) => {
    if (!isInputControlled) setInternalInputValue(newValue);
    onInputChange?.(event, newValue, reason);
  };

  const { slotProps: textFieldSlotProps, ...restTextFieldProps } =
    textFieldProps ?? {};
  const {
    onChange: onChangeProp,
    renderOption: renderOptionProp,
    filterOptions: filterOptionsProp,
    getOptionLabel: getOptionLabelProp,
    isOptionEqualToValue: isOptionEqualToValueProp,
    slotProps: autocompleteSlotProps,
    ...restAutocompleteProps
  } = autocompleteProps;

  const canLoadMore =
    hasMore &&
    !loadingMore &&
    (paginationMode === 'scroll' || paginationMode === 'both');
  const shouldShowLoadMore =
    hasMore && (paginationMode === 'button' || paginationMode === 'both');

  const handleLoadMore = useCallback(() => {
    if (!open || loadingMore || !hasMore) return;
    void load(query, page + 1);
  }, [hasMore, load, loadingMore, open, page, query]);

  const filterOptions = useCallback(
    (
      optionList: Array<TOption | LoadMoreOption>,
      state: FilterOptionsState<TOption>,
    ) => {
      const baseOptions = optionList.filter(
        (option) => !isLoadMoreOption(option),
      );
      const filtered = filterOptionsProp
        ? (filterOptionsProp(baseOptions as TOption[], state) as Array<
            TOption | LoadMoreOption
          >)
        : (baseOptions as Array<TOption | LoadMoreOption>);
      if (shouldShowLoadMore) {
        return [...filtered, loadMoreOption];
      }
      return filtered;
    },
    [filterOptionsProp, isLoadMoreOption, loadMoreOption, shouldShowLoadMore],
  );

  const getOptionLabel = useCallback(
    (option: TOption | LoadMoreOption) => {
      if (isLoadMoreOption(option)) return option.label;
      if (getOptionLabelProp) {
        return getOptionLabelProp(option as TOption);
      }
      return String(option);
    },
    [getOptionLabelProp, isLoadMoreOption],
  );

  const isOptionEqualToValue = useCallback(
    (option: TOption | LoadMoreOption, value: TOption | LoadMoreOption) => {
      if (isLoadMoreOption(option) || isLoadMoreOption(value)) return false;
      return isOptionEqualToValueProp
        ? isOptionEqualToValueProp(option as TOption, value as TOption)
        : option === value;
    },
    [isLoadMoreOption, isOptionEqualToValueProp],
  );

  const handleChange: AutocompleteAsyncProps<TOption, Multiple>['onChange'] = (
    event,
    value,
    reason,
    details,
  ) => {
    if (Array.isArray(value)) {
      const containsLoadMore = value.some((option) => isLoadMoreOption(option));
      if (containsLoadMore) {
        handleLoadMore();
        if (!isOpenControlled) setInternalOpen(true);
        const filtered = value.filter((option) => !isLoadMoreOption(option));
        onChangeProp?.(event, filtered as any, reason, details);
        return;
      }
      onChangeProp?.(event, value, reason, details);
      return;
    }
    if (isLoadMoreOption(value)) {
      handleLoadMore();
      if (!isOpenControlled) setInternalOpen(true);
      return;
    }
    onChangeProp?.(event, value, reason, details);
  };

  const listboxSlotProps = autocompleteSlotProps?.listbox as
    | React.HTMLAttributes<HTMLUListElement>
    | undefined;
  const handleListboxScroll: React.UIEventHandler<HTMLUListElement> = (
    event,
  ) => {
    listboxSlotProps?.onScroll?.(event);
    if (!canLoadMore) return;
    const listbox = event.currentTarget;
    if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 8) {
      handleLoadMore();
    }
  };

  const mergedSlotProps = {
    ...autocompleteSlotProps,
    listbox: {
      ...listboxSlotProps,
      onScroll: handleListboxScroll,
    },
  } as AutocompleteProps<
    TOption | LoadMoreOption,
    Multiple,
    false,
    false
  >['slotProps'];

  return (
    <MUIAutocomplete<TOption | LoadMoreOption, Multiple, false, false>
      {...(restAutocompleteProps as AutocompleteProps<
        TOption | LoadMoreOption,
        Multiple,
        false,
        false
      >)}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      options={options as Array<TOption | LoadMoreOption>}
      loading={loading || loadingMore}
      noOptionsText={noOptionsText}
      inputValue={currentInputValue}
      onInputChange={handleInputChange}
      onChange={
        handleChange as AutocompleteProps<
          TOption | LoadMoreOption,
          Multiple,
          false,
          false
        >['onChange']
      }
      filterOptions={
        filterOptions as AutocompleteProps<
          TOption | LoadMoreOption,
          Multiple,
          false,
          false
        >['filterOptions']
      }
      getOptionLabel={
        getOptionLabel as AutocompleteProps<
          TOption | LoadMoreOption,
          Multiple,
          false,
          false
        >['getOptionLabel']
      }
      isOptionEqualToValue={
        isOptionEqualToValue as AutocompleteProps<
          TOption | LoadMoreOption,
          Multiple,
          false,
          false
        >['isOptionEqualToValue']
      }
      slotProps={mergedSlotProps}
      renderInput={(params) => {
        const { InputProps, InputLabelProps, ...restParams } = params;
        return (
          <TextField
            {...restParams}
            {...restTextFieldProps}
            label={label}
            placeholder={placeholder}
            slotProps={{
              ...textFieldSlotProps,
              input: { ...InputProps, ...textFieldSlotProps?.input },
              inputLabel: {
                ...InputLabelProps,
                ...textFieldSlotProps?.inputLabel,
              },
            }}
          />
        );
      }}
      renderOption={(props, option, state, ownerState) => {
        const { key, ...restProps } = props;
        if (isLoadMoreOption(option)) {
          return (
            <li
              key={key}
              {...restProps}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                handleLoadMore();
              }}
              style={{
                color: 'var(--nn-color-text)',
              }}
            >
              {loadingMore ? 'Loading...' : option.label}
            </li>
          );
        }
        if (renderOptionProp) {
          return renderOptionProp(
            { key, ...restProps },
            option as TOption,
            state,
            ownerState as any,
          );
        }
        return (
          <li key={key} {...restProps}>
            {getOptionLabel(option as TOption)}
          </li>
        );
      }}
    />
  );
}
